/**
 * @file ConsentBannerGroup.tsx
 * @description Группа «Баннер» панели «Счётчики и cookie» (phase19.16,
 * партия Р3г, v0.19.62; SB134; db-design-r3g §2.3; uiux-r3 §3.6):
 *  - вид на ПК: «Полоса внизу» (умолчание, как сейчас) или «Карточка в углу»;
 *    у карточки — ширина числом 320–480 px и угол; на экране уже 640 px
 *    баннер всегда полоса;
 *  - заливка и цвет текста — как у корзины (роли темы, свой цвет); контраст
 *    ниже 4,5:1 — предупреждение `consentBannerWarnings` (то же правило, что
 *    в окне публикации), не запрет;
 *  - «Без счётчиков показывать уведомление» (часть COUNTERS).
 * Вид баннера — раздел темы (часть THEME).
 */

import {
  DEFAULT_SITE_CONSENT_BANNER,
  SITE_CONSENT_BANNER_LAYOUTS,
  SITE_CONSENT_BANNER_LAYOUT_LABELS,
  SITE_CONSENT_BANNER_SIDES,
  SITE_CONSENT_BANNER_SIDE_LABELS,
  SITE_FILL_ROLES,
  SITE_TEXT_COLOR_ROLES,
  SITE_THEME_LIMITS,
  consentBannerWarnings,
  resolveSurfaceFill,
  type SiteConsentBannerLayout,
} from "@chaiberi/shared";
import type { ReactNode } from "react";

import { NoticeStrip } from "../../ui/NoticeStrip";
import { FieldHint, GroupField, SettingsGroup } from "../../ui/settings-groups";
import { ColorField } from "../../site-fields/ColorField";
import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { OptionCards, type OptionCard } from "../OptionCards";
import { Segmented } from "../Segmented";
import { useCounters } from "./counters-context";
import { SwitchRow } from "./SwitchRow";
import { sameValue } from "../../../lib/site-view/cart-look";
import { WITHOUT_COUNTERS_HINT, bannerSummary } from "../../../lib/site-view/counters-form";
import { TEXT_COLOR_ROLE_LABELS } from "../../../lib/site-fields/text-color-labels";
import { FILL_ROLE_LABELS } from "../../../lib/site-view/surface-fill-labels";

const W = SITE_THEME_LIMITS.consentBanner.cardWidth;

/** Мини-схемы вида баннера. */
const LAYOUT_PREVIEW: Readonly<Record<SiteConsentBannerLayout, ReactNode>> = {
  bar: (
    <span className="relative block h-8 w-14 rounded bg-surface-lowest">
      <span className="absolute inset-x-0 bottom-0 h-2 rounded-b bg-on-surface-variant/60" />
    </span>
  ),
  card: (
    <span className="relative block h-8 w-14 rounded bg-surface-lowest">
      <span className="absolute bottom-1 right-1 h-4 w-5 rounded-sm bg-on-surface-variant/60" />
    </span>
  ),
};

const LAYOUT_OPTIONS: readonly OptionCard<SiteConsentBannerLayout>[] = SITE_CONSENT_BANNER_LAYOUTS.map((value) => ({
  value,
  label: SITE_CONSENT_BANNER_LAYOUT_LABELS[value],
  preview: LAYOUT_PREVIEW[value],
}));

const SIDE_OPTIONS = SITE_CONSENT_BANNER_SIDES.map((value) => ({ value, label: SITE_CONSENT_BANNER_SIDE_LABELS[value] }));

export function ConsentBannerGroup() {
  const { counters, banner, theme, noticeAllowed, update, updateBanner, previewBanner } = useCounters();
  const D = DEFAULT_SITE_CONSENT_BANNER;
  const warnings = consentBannerWarnings(banner, theme);
  const reset = <K extends "fill" | "text">(key: K) => (sameValue(banner[key], D[key]) ? undefined : () => updateBanner({ [key]: D[key] }));

  return (
    <SettingsGroup id="banner" title="Баннер" hint="Вид баннера согласия на cookie." summary={bannerSummary(banner, counters, noticeAllowed)} status={warnings.length > 0 ? "warning" : undefined}>
      <div className="flex min-w-0 flex-col gap-4">
        <GroupField label="Вид на компьютере">
          <OptionCards aria-label="Вид баннера на компьютере" columns={2} options={LAYOUT_OPTIONS} value={banner.layout} onChange={(layout) => updateBanner({ layout })} />
          <FieldHint className="mt-0">На телефоне и экранах уже 640 px баннер всегда полоса.</FieldHint>
        </GroupField>
        {banner.layout === "card" ? (
          <>
            <NumberSliderField
              label="Ширина карточки"
              value={banner.cardWidth}
              inherit={{ value: W.default, label: `${W.default} px` }}
              min={W.min}
              max={W.max}
              step={1}
              integer
              unit="px"
              onChange={(v) => updateBanner({ cardWidth: v ?? W.default })}
              onPreview={(v) => previewBanner({ cardWidth: v ?? W.default })}
            />
            <GroupField label="Угол">
              <Segmented aria-label="Угол карточки" options={SIDE_OPTIONS} value={banner.cardSide} onChange={(cardSide) => updateBanner({ cardSide })} />
            </GroupField>
          </>
        ) : null}
        <ColorField
          mode="fill"
          label="Заливка"
          value={banner.fill}
          roles={SITE_FILL_ROLES}
          labels={FILL_ROLE_LABELS}
          colors={theme.colors}
          contrast={false}
          inheritLabel="как сейчас"
          onReset={reset("fill")}
          onChange={(fill) => (fill ? updateBanner({ fill }) : undefined)}
        />
        <ColorField
          mode="text"
          label="Цвет текста"
          value={banner.text}
          roles={SITE_TEXT_COLOR_ROLES}
          labels={TEXT_COLOR_ROLE_LABELS}
          colors={theme.colors}
          elementBg={resolveSurfaceFill(banner.fill, theme).background}
          // Контраст — строкой ниже по правилу окна публикации (consentBannerWarnings), здесь не повторяем.
          contrast={false}
          inheritLabel="как сейчас"
          onReset={reset("text")}
          onChange={(text) => updateBanner({ text })}
        />
        {warnings.map((w) => (
          <NoticeStrip key={w.code} tone="warning">
            {/* Своя подпись «баннера cookie», чтобы не спорить с «контрастом цветов темы» (QA D6). */}
            Контраст баннера cookie ниже 4,5:1{w.value !== undefined ? ` (${w.value.toFixed(1).replace(".", ",")}:1)` : ""} — возьмите читаемый цвет текста или заливки.
          </NoticeStrip>
        ))}
      </div>
      <SwitchRow
        label="Без счётчиков показывать уведомление"
        hint={WITHOUT_COUNTERS_HINT}
        checked={counters.noticeWithoutCounters}
        onChange={(noticeWithoutCounters) => update({ noticeWithoutCounters })}
      />
    </SettingsGroup>
  );
}
